import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Card, CloseButton, Text, CardHeader, CardBody, Flex } from '@chakra-ui/react';

import {
    getParameterDisplayName,
    getLatestObservation,
    getRelativeTime,
    getStationName,
    getParameterTrend,
    getParameterUnit,
    getObservationInPreferredUnits,
} from '../../utils/helpers';
import { ParameterChart } from '../../components/stations/ParameterChart';
import { ParameterTrend } from '../../components/stations/ParameterTrend';
import { TIDE_PARAMETER, OLD_DATA_THRESHOLD } from '../../utils/constants';
import { CardinalDirectionIndicator } from '../../components/CardinalDirectionIndicator';
import { OldData } from '../stations/OldData';
import Observation from '../../components/stations/Observation';

const StationPreview = (props) => {
    const {
        selectedParameter,
        selectedStation,
        setSelectedStation,
        stationMetadata,
        displayParameter,
        preferredUnitSystem,
    } = props;

    const stationName = getStationName(stationMetadata);
    const currentObservation = getLatestObservation(selectedStation.obsData);
    const stationUpdated = getRelativeTime(currentObservation?.time);

    console.log('observations', selectedStation.obsData);

    const parameterData = selectedStation.obsData
        .filter((observation) => observation[selectedParameter])
        .map((observation) => ({
            [selectedParameter]: observation[selectedParameter],
            time: observation.time,
        }));

    // because currentObservation is always in the past, but tidal predictions go into the future,
    // we want to consider the actual last obs available for tides
    const latestObsDate = selectedParameter === TIDE_PARAMETER
        ? new Date(selectedStation.obsData[selectedStation.obsData.length - 1].time)
        : new Date(currentObservation?.time);

    const now = new Date();
    const oldData = now - latestObsDate > OLD_DATA_THRESHOLD;

    const latestParameterObservations = getLatestObservation(parameterData, selectedParameter);
    const unit = getParameterUnit(stationMetadata, selectedParameter);
    const latestParameterObservation = getObservationInPreferredUnits(selectedParameter, latestParameterObservations?.[selectedParameter], unit, preferredUnitSystem);

    const moreLinkPath = selectedParameter && selectedParameter !== TIDE_PARAMETER
        ? `/stations/${selectedStation.datasetID}/${selectedParameter}`
        : `/stations/${selectedStation.datasetID}`;

    const trend = getParameterTrend(selectedStation.obsData, selectedParameter);

    return (
        <Card mx={2} textAlign='left'>
            <CardHeader position='relative'>
                <Text fontWeight={700} fontSize={'18px'} pr={7}>
                    {stationName}
                </Text>
                <CloseButton style={{ position: 'absolute', right: 10, top: 10 }} onClick={() => setSelectedStation(null)} />
            </CardHeader>
            <CardBody pt={0}>
                {oldData && (
                    <OldData />
                )}
                {displayParameter && selectedParameter && latestParameterObservation?.value && (
                    <>
                        <Text mt={0} fontWeight={600} fontSize={16}>
                            {getParameterDisplayName(stationMetadata, selectedParameter)}
                        </Text>
                        <Text fontWeight={600} fontSize={20}>
                            <Flex>
                                <Observation
                                    value={latestParameterObservation.value}
                                    unit={latestParameterObservation.unit}
                                    showUnit={true}
                                />
                                {unit === '°' &&
                                    <CardinalDirectionIndicator degrees={latestParameterObservation.value} directionSize={20} arrowSize={25} />
                                }
                            </Flex>
                            {selectedParameter === TIDE_PARAMETER && (
                                <span style={{ fontStyle: 'italic', fontSize: 12, fontWeight: 400, color: '#718096' }}>{' '}Latest</span>
                            )}
                        </Text>
                    </>
                )}
                {/* TODO: get the parameter name here, but it's not available in this dataset's metadata, so it will need to come from the list of all parameters */}
                {displayParameter && selectedParameter && !latestParameterObservation?.value && (
                    <Text mt={0} fontWeight={600} fontSize={16}>
                        Selected parameter unavailable at this station.
                    </Text>
                )}
                <Text margin={'8px 0'} fontSize={14} color={'gray.700'}>
                    {displayParameter && selectedParameter && latestParameterObservation?.value && (
                        <>
                            {trend && <ParameterTrend trend={trend} />}
                            {' '}
                            {getRelativeTime(latestParameterObservations?.time)}
                        </>
                    )}
                    {!displayParameter && (
                        <>
                            Last updated
                            {' '}
                            {stationUpdated}
                        </>
                    )}
                </Text>
                {displayParameter && selectedParameter && <ParameterChart parameter={selectedParameter} stationMetadata={stationMetadata} parameterObservations={parameterData} simpleChart={true} />}
                <Flex justify='flex-end' mt={4}>
                    <RouterLink to={moreLinkPath}>
                        <Button bg={'teal.500'}>
                            View
                            {' '}
                            {selectedParameter
                                ? 'More'
                                : 'Station'
                            }
                        </Button>
                    </RouterLink>
                </Flex>
            </CardBody>
        </Card>
    );
};

export default StationPreview;
